/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Share-location modal — Figma 3629:6819 / 3629:6927 / 3629:7114.
//
// A 739×468 rounded-2xl two-column surface:
//   • LEFT sidebar (283px, bg #15171C): title + close, search input,
//     segmented "Static pin" / "Current location" tabs, a results list
//     ("Nearby locations" / "Search results"), and a footer Cancel/Share row.
//   • RIGHT map pane (456px, bg #111318): a selected-location chip top-left,
//     a locate/zoom button stack top-right, and the static-map preview with a
//     center pin.
//
// When `liveMode` is set the primary Share button opens the live-duration
// sub-modal (Figma 3629:7114) first; otherwise it shares a static point.
//
// Tailwind/NativeWind classes lead per the repo styling rule; exact Figma
// values use arbitrary-value classes. Inline `style` is reserved for the
// runtime-computed map image URI only.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Image,
  Modal as RNModal,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { MapPin, Minus, Navigation, Plus, Search, X } from 'lucide-react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { buildStaticMapUrl } from '../chat/attachments/LocationAttachmentCard';
import { LocationResultRow } from './LocationResultRow';
import { LiveDurationModal } from './LiveDurationModal';
import { useShareLocationViewModel } from './useShareLocationViewModel';
import { shareLocationStrings } from './strings';
import type { Location, ShareLocationModalProps } from './types';

// The right-pane map preview pixel box. The static-map URL is built at this
// exact size so the request matches what is rendered.
const PREVIEW_WIDTH = 408; // 456 pane - 24 padding each side
const PREVIEW_HEIGHT = 360;
const PREVIEW_ZOOM = 15;
// Static-map zoom is clamped to a sane street→region range. `buildStaticMapUrl`
// re-clamps to the provider-valid 1–20, but the +/− buttons stop here so the
// preview never requests a degenerate world/building-level tile.
const MIN_ZOOM = 3;
const MAX_ZOOM = 19;

export const ShareLocationModal: React.FC<ShareLocationModalProps> = ({
  visible,
  callbacks,
  defaultLiveDurationSeconds = 60 * 60,
  conversationName,
  liveMode = false,
}) => {
  const vm = useShareLocationViewModel(callbacks, defaultLiveDurationSeconds);
  // Scheme-aware icon colors — lucide takes a string `color` prop, so the
  // `dark:` class variant can't reach it. Pair manually so glyphs stay
  // legible on the light-mode (white) and dark-mode (panel) surfaces.
  const scheme = useColorScheme();
  const iconPrimary = scheme === 'dark' ? '#F5F6F8' : '#1C1C1E';
  const iconMuted = scheme === 'dark' ? '#B5BAC3' : '#6B7280';
  const [sharing, setSharing] = useState(false);
  const [durationOpen, setDurationOpen] = useState(false);
  // Local preview zoom for the static map; the +/− buttons step it within
  // [MIN_ZOOM, MAX_ZOOM] and it feeds the static-map URL builder.
  const [zoom, setZoom] = useState(PREVIEW_ZOOM);

  // Figma 3629:6773 — "Opens with the current location of the user". On
  // open, eagerly resolve the device location so the map + chip populate
  // by default (instead of a blank pane) without the user touching the
  // "Current location" tab. No-ops when the host doesn't supply a resolver.
  const resolveCurrent = callbacks.onResolveCurrentLocation;
  useEffect(() => {
    if (
      visible &&
      resolveCurrent !== undefined &&
      vm.selected === null &&
      vm.currentLocation === null &&
      !vm.resolvingCurrent
    ) {
      vm.useCurrentLocation();
    }
    // Only re-run on open/close — `vm` is stable per render and the guards
    // above prevent a resolve loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, resolveCurrent]);

  const ready = vm.buildShareResult() !== null;

  // The point currently in focus for the preview: an explicit search
  // selection wins, else the resolved current location. Coordinates stay in
  // memory only and are never logged (Hard rule 3).
  const target: Location | null = vm.selected
    ? vm.selected.location
    : vm.currentLocation;
  const previewUrl = useMemo(
    () =>
      target
        ? buildStaticMapUrl(target.lat, target.lng, {
            width: PREVIEW_WIDTH,
            height: PREVIEW_HEIGHT,
            zoom,
          })
        : null,
    [target, zoom],
  );

  const zoomIn = useCallback(
    () => setZoom((z) => Math.min(MAX_ZOOM, z + 1)),
    [],
  );
  const zoomOut = useCallback(
    () => setZoom((z) => Math.max(MIN_ZOOM, z - 1)),
    [],
  );
  // Locate-me: re-center on the resolved current location (switches the
  // viewmodel to the "current" source) and reset to the default zoom.
  const locateMe = useCallback(() => {
    vm.useCurrentLocation();
    setZoom(PREVIEW_ZOOM);
  }, [vm]);
  const previewLabel = vm.selected?.label ?? target?.label ?? undefined;
  const previewAddress = vm.selected?.address ?? target?.address ?? undefined;

  // Emit the share result with the (optional) live duration. The viewmodel's
  // `liveEnabled` drives the result kind, so we set it before building.
  const emitShare = useCallback(
    async (durationSeconds?: number) => {
      if (durationSeconds !== undefined) {
        vm.setLiveDurationSeconds(durationSeconds);
        vm.setLiveEnabled(true);
      } else if (!liveMode) {
        vm.setLiveEnabled(false);
      }
      const result = vm.buildShareResult();
      if (!result) {
        return;
      }
      // Override the kind/duration deterministically — `buildShareResult`
      // reads the (possibly not-yet-flushed) state, so we compose the final
      // payload here from the explicit call.
      const finalResult =
        durationSeconds !== undefined
          ? { ...result, kind: 'live' as const, liveDurationSeconds: durationSeconds }
          : { ...result, kind: 'point' as const };
      setSharing(true);
      try {
        await callbacks.onShare(finalResult);
      } finally {
        setSharing(false);
      }
    },
    [callbacks, liveMode, vm],
  );

  const handleSharePress = useCallback(() => {
    if (!ready) {
      return;
    }
    if (liveMode) {
      setDurationOpen(true);
      return;
    }
    void emitShare();
  }, [emitShare, liveMode, ready]);

  const handleDurationConfirm = useCallback(
    (durationSeconds: number) => {
      setDurationOpen(false);
      void emitShare(durationSeconds);
    },
    [emitShare],
  );

  const trimmedQuery = vm.query.trim();
  const showingSearch = vm.tab === 'static' && trimmedQuery.length > 0;
  const resultsLabel = showingSearch
    ? shareLocationStrings.modal.searchResults
    : shareLocationStrings.modal.nearby;

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={callbacks.onClose}
      accessibilityViewIsModal
      accessibilityLabel={shareLocationStrings.modal.title}
    >
      <Pressable
        className="flex-1 items-center justify-center bg-black/60 p-md"
        onPress={callbacks.onClose}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="h-[468px] max-h-[90vh] w-full max-w-[739px] flex-row overflow-hidden rounded-2xl"
        >
          {/* ───────── LEFT SIDEBAR (283px) ───────── */}
          <View className="w-[283px] gap-3 bg-white dark:bg-dark-surface-panel p-3">
            {/* Title row */}
            <View className="flex-row items-center justify-between">
              <Text className="text-[18px] font-bold text-light-text-primary dark:text-white">
                {shareLocationStrings.modal.title}
              </Text>
              <Pressable
                onPress={callbacks.onClose}
                accessibilityRole="button"
                accessibilityLabel={shareLocationStrings.modal.close}
                className="h-7 w-7 items-center justify-center rounded-[12px]"
              >
                <X size={18} color={iconMuted} />
              </Pressable>
            </View>

            {/* Search input — shared design-system <Input> with a leading
                search glyph + a clear (X) affordance. */}
            <Input
              fullWidth
              value={vm.query}
              onChangeText={vm.setQuery}
              placeholder={shareLocationStrings.modal.searchPlaceholder}
              accessibilityLabel={shareLocationStrings.modal.searchPlaceholder}
              leftIcon={<Search size={14} color={iconMuted} />}
              {...(vm.query.length > 0
                ? {
                    rightIcon: (
                      <Pressable
                        onPress={() => vm.setQuery('')}
                        accessibilityRole="button"
                        accessibilityLabel={shareLocationStrings.modal.clear}
                        hitSlop={8}
                      >
                        <X size={18} color={iconMuted} />
                      </Pressable>
                    ),
                  }
                : {})}
            />

            {/* Segmented tabs */}
            <View className="flex-row gap-1 rounded-[16px] bg-light-surface-secondary dark:bg-dark-surface-app p-1">
              <Pressable
                onPress={() => vm.setTab('static')}
                accessibilityRole="tab"
                accessibilityLabel={shareLocationStrings.modal.tabStatic}
                accessibilityState={{ selected: vm.tab === 'static' }}
                className={`h-[33px] flex-1 items-center justify-center rounded-[8px] px-3 ${
                  vm.tab === 'static'
                    ? 'bg-light-surface-panel dark:bg-dark-surface-elevated'
                    : 'bg-transparent'
                }`}
              >
                <Text
                  className={`text-[12px] font-bold ${
                    vm.tab === 'static' ? 'text-light-text-primary dark:text-white' : 'text-[#717680]'
                  }`}
                >
                  {shareLocationStrings.modal.tabStatic}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => vm.setTab('current')}
                accessibilityRole="tab"
                accessibilityLabel={shareLocationStrings.modal.tabCurrent}
                accessibilityState={{ selected: vm.tab === 'current' }}
                className={`h-[33px] flex-1 items-center justify-center rounded-[8px] px-3 ${
                  vm.tab === 'current'
                    ? 'bg-light-surface-panel dark:bg-dark-surface-elevated'
                    : 'bg-transparent'
                }`}
              >
                <Text
                  className={`text-[12px] font-bold ${
                    vm.tab === 'current' ? 'text-light-text-primary dark:text-white' : 'text-[#717680]'
                  }`}
                >
                  {shareLocationStrings.modal.tabCurrent}
                </Text>
              </Pressable>
            </View>

            {/* Results section */}
            <View className="flex-1">
              <Text className="mb-1 text-[10px] text-light-text-muted dark:text-dark-text-muted">
                {resultsLabel}
              </Text>
              <ScrollView className="flex-1">
                {vm.tab === 'current' ? (
                  vm.resolvingCurrent ? (
                    <Text className="p-3 text-center text-[12px] text-light-text-muted dark:text-dark-text-muted">
                      {shareLocationStrings.modal.resolving}
                    </Text>
                  ) : vm.currentLocation ? (
                    <Text className="p-1 text-[12px] text-light-text-secondary dark:text-[#B5BAC3]">
                      {vm.currentLocation.address ??
                        vm.currentLocation.label ??
                        shareLocationStrings.card.defaultPlace}
                    </Text>
                  ) : (
                    <Text className="p-3 text-center text-[12px] text-light-text-muted dark:text-dark-text-muted">
                      {shareLocationStrings.modal.noSelection}
                    </Text>
                  )
                ) : trimmedQuery === '' && vm.results.length === 0 ? (
                  <Text className="p-3 text-center text-[12px] text-light-text-muted dark:text-dark-text-muted">
                    {shareLocationStrings.modal.typeToSearch}
                  </Text>
                ) : vm.results.length === 0 && !vm.searching ? (
                  <Text className="p-3 text-center text-[12px] text-light-text-muted dark:text-dark-text-muted">
                    {shareLocationStrings.modal.noResults}
                  </Text>
                ) : (
                  vm.results.map((result) => (
                    <LocationResultRow
                      key={result.id}
                      result={result}
                      selected={vm.selected?.id === result.id}
                      onSelect={vm.selectResult}
                    />
                  ))
                )}
              </ScrollView>
            </View>

            {/* Footer actions — shared design-system <Button>s. */}
            <View className="flex-row items-center justify-end gap-3">
              <Button
                variant="text"
                size="small"
                onPress={callbacks.onClose}
                accessibilityLabel={shareLocationStrings.modal.cancel}
              >
                {shareLocationStrings.modal.cancel}
              </Button>
              <Button
                variant="primary"
                size="small"
                onPress={handleSharePress}
                disabled={!ready || sharing}
                loading={sharing}
                accessibilityLabel={shareLocationStrings.modal.share}
              >
                {shareLocationStrings.modal.share}
              </Button>
            </View>
          </View>

          {/* ───────── RIGHT MAP PANE (456px) ───────── */}
          <View className="w-[456px] bg-light-surface-secondary dark:bg-dark-surface-app p-6">
            <View className="relative flex-1 overflow-hidden rounded-[14px]">
              {/* Map image (runtime URI → inline style allowed) */}
              {target && previewUrl ? (
                <Image
                  source={{ uri: previewUrl }}
                  className="absolute inset-0 h-full w-full"
                  accessibilityIgnoresInvertColors
                  accessibilityLabel={
                    previewLabel
                      ? `${shareLocationStrings.modal.mapPreviewLabel}: ${previewLabel}`
                      : shareLocationStrings.modal.mapPreviewLabel
                  }
                />
              ) : (
                // No tile (no maps key, or location still resolving): a
                // muted map-style placeholder so the pane never reads as a
                // broken void. A real street map renders here once
                // VITE_MAPS_KEY + VITE_MAPS_STATIC_URL (or a provider token)
                // are configured.
                <View className="absolute inset-0 items-center justify-center bg-light-surface-tertiary dark:bg-[#14161B]">
                  <MapPin
                    size={32}
                    color={scheme === 'dark' ? '#2F333B' : '#C9CDD6'}
                    fill={scheme === 'dark' ? '#2F333B' : '#C9CDD6'}
                  />
                  {vm.resolvingCurrent ? (
                    <Text className="mt-2 text-[12px] text-light-text-muted dark:text-dark-text-muted">
                      {shareLocationStrings.modal.resolving}
                    </Text>
                  ) : null}
                </View>
              )}

              {/* Center pin (only once a location is in focus) */}
              {target ? (
                <View
                  className="absolute inset-0 items-center justify-center"
                  pointerEvents="none"
                >
                  <MapPin size={27} color="#537EFF" fill="#537EFF" />
                </View>
              ) : null}

              {/* Selected-location chip (top-left) */}
              {target && (previewLabel || previewAddress) ? (
                <View
                  className="absolute left-3 top-3 flex-row items-center gap-2.5 rounded-[14px] bg-white dark:bg-dark-surface-panel px-3 py-2"
                  pointerEvents="none"
                >
                  <View className="h-7 w-7 items-center justify-center rounded-[8px] bg-light-accent-brand dark:bg-dark-accent-brand">
                    <MapPin size={16} color="#FFFFFF" />
                  </View>
                  <View>
                    {previewLabel ? (
                      <Text
                        className="text-[14px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
                        numberOfLines={1}
                      >
                        {previewLabel}
                      </Text>
                    ) : null}
                    {previewAddress ? (
                      <Text
                        className="text-[12px] text-light-text-secondary dark:text-[#B5BAC3]"
                        numberOfLines={1}
                      >
                        {previewAddress}
                      </Text>
                    ) : null}
                  </View>
                </View>
              ) : null}

              {/* Locate / zoom button stack (top-right) */}
              <View className="absolute right-3 top-3 gap-2">
                <Pressable
                  onPress={locateMe}
                  accessibilityRole="button"
                  accessibilityLabel={shareLocationStrings.modal.locateMe}
                  className="h-[38px] w-[38px] items-center justify-center rounded-full bg-white dark:bg-dark-surface-panel"
                >
                  <Navigation size={18} color={iconPrimary} />
                </Pressable>
                <Pressable
                  onPress={zoomIn}
                  disabled={zoom >= MAX_ZOOM}
                  accessibilityRole="button"
                  accessibilityLabel={shareLocationStrings.modal.zoomIn}
                  accessibilityState={{ disabled: zoom >= MAX_ZOOM }}
                  className={`h-[38px] w-[38px] items-center justify-center rounded-full bg-white dark:bg-dark-surface-panel ${
                    zoom >= MAX_ZOOM ? 'opacity-50' : ''
                  }`}
                >
                  <Plus size={18} color={iconPrimary} />
                </Pressable>
                <Pressable
                  onPress={zoomOut}
                  disabled={zoom <= MIN_ZOOM}
                  accessibilityRole="button"
                  accessibilityLabel={shareLocationStrings.modal.zoomOut}
                  accessibilityState={{ disabled: zoom <= MIN_ZOOM }}
                  className={`h-[38px] w-[38px] items-center justify-center rounded-full bg-white dark:bg-dark-surface-panel ${
                    zoom <= MIN_ZOOM ? 'opacity-50' : ''
                  }`}
                >
                  <Minus size={18} color={iconPrimary} />
                </Pressable>
              </View>
            </View>
          </View>
        </Pressable>
      </Pressable>

      {/* Live-duration sub-modal — opened by the Share button in live mode. */}
      <LiveDurationModal
        visible={durationOpen}
        conversationName={conversationName}
        defaultDurationSeconds={defaultLiveDurationSeconds}
        onCancel={() => setDurationOpen(false)}
        onConfirm={handleDurationConfirm}
      />
    </RNModal>
  );
};
